/**
 * Default image fallback helper.
 *
 * When an API response doesn't include an image URL (null / undefined / empty),
 * this function returns the appropriate locally-hosted default image.
 *
 * Default images are stored under /public/defaults/:
 *   /defaults/property.jpg   – used for property cards, banners
 *   /defaults/destination.jpg – destination/experience images
 *   /defaults/service.jpg    – service provider images
 *   /defaults/avatar.png     – user avatars
 *   /defaults/banner.jpg     – hero/section banners
 */

export type ImageContext =
  | "property"
  | "destination"
  | "service"
  | "avatar"
  | "banner";

const DEFAULTS: Record<ImageContext, string> = {
  property: "/defaults/property.jpg",
  destination: "/defaults/destination.jpg",
  service: "/defaults/service.jpg",
  avatar: "/defaults/avatar.png",
  banner: "/defaults/banner.jpg",
};

/**
 * Returns `url` when it is a non-empty string, otherwise returns the
 * context-appropriate default image path.
 */
export function getImageWithFallback(
  url: string | null | undefined,
  context: ImageContext,
): string {
  if (url && url.trim().length > 0) return safeImageUrl(url);
  return DEFAULTS[context];
}

/**
 * The API used to return image URLs as http:// while the site is https://.
 * iPhones block such "mixed content" images (they showed as "?"), so any
 * http:// URL on a real domain is upgraded to https:// (local dev untouched).
 */
export function safeImageUrl(url: string): string {
  if (!url.startsWith("http://")) return url;
  const host = url.slice(7).split(/[/:]/)[0];
  if (host === "localhost" || /^(127|10|192\.168)\./.test(host)) return url;
  return `https://${url.slice(7)}`;
}

/**
 * React `onError` handler — replaces a broken <img> src with the default.
 *
 * Usage:
 *   <img src={src} onError={createImgErrorHandler("property")} />
 */
export function createImgErrorHandler(context: ImageContext) {
  return (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const fallback = DEFAULTS[context];
    if (img.src !== window.location.origin + fallback) {
      img.src = fallback;
    }
  };
}
